import { BOOKS_TIME_ZONE, reportTimeZone } from '../report-range'

interface ReportMetaProps {
  /** `1 – 31 Aug 2026`, from the resolved range. */
  rangeLabel: string
  /** The outlet in scope, or null for every one the caller may read. */
  scopeLabel: string
  /** Rows on this page, and rows in the whole result. */
  shown?: number
  total?: number
  /**
   * The zone the SERVER cut the days in, for a report that takes trading days.
   *
   * When given, the browser's own zone is irrelevant — the boundaries were not
   * computed here — so it is stated plainly instead of being checked against
   * the laptop's clock.
   */
  serverZone?: string
  /** When the server read the figures. ISO. */
  generatedAt?: string
  /** The period reaches today, so its last day can still move. */
  includesToday?: boolean
}

/**
 * The line under the filters that says what you are actually looking at.
 *
 * Every figure below it is meaningless without these four facts — which period,
 * which outlets, in whose clock, and read when. A report screenshotted into a
 * message thread without them is a number somebody will act on and nobody can
 * reproduce, which is the single most common way a figure ends up being wrong in
 * a way that survives.
 *
 * The timezone is named rather than assumed. The books are kept in Phnom Penh;
 * a laptop set to another zone computes day boundaries somewhere else, and this
 * is the one line that would let a manager notice that before signing anything.
 */
export function ReportMeta({
  rangeLabel,
  scopeLabel,
  shown,
  total,
  serverZone,
  generatedAt,
  includesToday,
}: ReportMetaProps) {
  const zone = serverZone ?? reportTimeZone()
  const isElsewhere = !serverZone && zone !== BOOKS_TIME_ZONE

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      <span className="text-muted">
        {rangeLabel} · {scopeLabel} · USD
      </span>

      <span className="text-muted">·</span>

      <span className={isElsewhere ? 'text-amber-700 dark:text-amber-400' : 'text-muted'}>
        {isElsewhere ? `Days start at midnight ${zone}, not ${BOOKS_TIME_ZONE}` : zone}
      </span>

      {/* The legacy pack printed neither of these, and it is how 14 October
          came to read $36.70 in one export and $16.10 in another with nothing
          to say which was older. A figure for a day still trading is a
          snapshot, and the line says so. */}
      {generatedAt && (
        <>
          <span className="text-muted">·</span>
          <span className="text-muted">read {readAt(generatedAt)}</span>
        </>
      )}

      {includesToday && (
        <>
          <span className="text-muted">·</span>
          <span className="text-amber-700 dark:text-amber-400">today is still trading</span>
        </>
      )}

      {shown !== undefined && total !== undefined && total > shown && (
        <>
          <span className="text-muted">·</span>
          <span className="text-muted">
            showing {shown} of {total}
          </span>
        </>
      )}
    </div>
  )
}

/**
 * `14:32`, or `14 Oct, 14:32` when it was not today.
 *
 * Read in the books' zone, like every other clock time on these reports, so the
 * line agrees with the till's receipts rather than with the laptop.
 */
function readAt(iso: string): string {
  const at = new Date(iso)

  if (Number.isNaN(at.getTime())) return '—'

  const sameDay =
    at.toLocaleDateString('en-CA', { timeZone: BOOKS_TIME_ZONE }) ===
    new Date().toLocaleDateString('en-CA', { timeZone: BOOKS_TIME_ZONE })

  return at.toLocaleString(undefined, {
    timeZone: BOOKS_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    ...(sameDay ? {} : { day: 'numeric', month: 'short' }),
  })
}
